/**
 * SKOP Creator Reputation System
 *
 * Track and display creator history and reputation scores.
 */

// ============================================================================
// Types
// ============================================================================

export interface CreatorProfile {
  address: string;
  totalTokensCreated: number;
  rugCount: number;
  successfulTokens: number;
  averageHoldTime: number; // Hours before creator sold
  totalVolume: number;
  firstTokenDate: number;
  lastTokenDate: number;
  reputationScore: number; // 0-100
  badges: CreatorBadge[];
  verified: boolean;
  verificationNote?: string;
}

export type CreatorBadge =
  | 'trusted'
  | 'new'
  | 'serial_rugger'
  | 'diamond_hands'
  | 'paper_hands'
  | 'high_volume'
  | 'verified';

export interface CreatorToken {
  address: string;
  name: string;
  symbol: string;
  createdAt: number;
  platform: string;
  status: 'active' | 'graduated' | 'rugged' | 'dead';
  peakMarketCap?: number;
  currentMarketCap?: number;
  creatorHoldPercent: number;
  creatorSoldAt?: number;
  profitPercent?: number;
}

export interface CreatorStats {
  avgRugTime: number; // Average time in hours before rug
  avgHoldTime: number; // Average time creator holds
  successRate: number; // % of tokens that didn't rug
  avgProfitPercent: number;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
}

// ============================================================================
// Badge Definitions
// ============================================================================

export const BADGE_INFO: Record<CreatorBadge, { label: string; color: string; description: string }> = {
  trusted: {
    label: 'Trusted',
    color: '#00ff88',
    description: 'Creator has a history of legitimate projects',
  },
  new: {
    label: 'New Creator',
    color: '#888',
    description: 'First token from this wallet',
  },
  serial_rugger: {
    label: 'Serial Rugger',
    color: '#ff3b3b',
    description: 'Multiple rugged tokens in history',
  },
  diamond_hands: {
    label: 'Diamond Hands',
    color: '#00f0ff',
    description: 'Usually holds tokens for extended periods',
  },
  paper_hands: {
    label: 'Paper Hands',
    color: '#ffb800',
    description: 'Typically sells quickly after launch',
  },
  high_volume: {
    label: 'High Volume',
    color: '#ff00ff',
    description: 'Has launched many tokens',
  },
  verified: {
    label: 'Verified',
    color: '#00f0ff',
    description: 'Identity verified by SKōP team',
  },
};

// ============================================================================
// Reputation Calculation
// ============================================================================

/**
 * Calculate reputation score from creator history
 */
export function calculateReputationScore(profile: Partial<CreatorProfile>): number {
  let score = 50; // Start at neutral

  const {
    totalTokensCreated = 0,
    rugCount = 0,
    successfulTokens = 0,
    averageHoldTime = 0,
  } = profile;

  // Rug penalty: -15 points per rug
  score -= rugCount * 15;

  // Success bonus: +5 points per successful token
  score += successfulTokens * 5;

  // Hold time bonus/penalty
  if (averageHoldTime < 1) {
    score -= 20; // Sells within an hour = suspicious
  } else if (averageHoldTime > 24) {
    score += 10; // Holds for at least a day
  } else if (averageHoldTime > 168) {
    score += 20; // Holds for a week
  }

  // New creator penalty (lack of history)
  if (totalTokensCreated === 1) {
    score -= 10;
  }

  // Serial launcher penalty
  if (totalTokensCreated > 20) {
    score -= 10; // Too many tokens suggests low effort
  }

  // Clamp to 0-100
  return Math.max(0, Math.min(100, score));
}

/**
 * Determine badges based on profile
 */
export function determineBadges(profile: Partial<CreatorProfile>): CreatorBadge[] {
  const badges: CreatorBadge[] = [];

  const {
    totalTokensCreated = 0,
    rugCount = 0,
    averageHoldTime = 0,
    verified = false,
  } = profile;

  if (verified) {
    badges.push('verified');
  }

  if (totalTokensCreated === 1) {
    badges.push('new');
  }

  if (rugCount >= 2) {
    badges.push('serial_rugger');
  }

  if (averageHoldTime > 168 && totalTokensCreated > 3) {
    badges.push('diamond_hands');
  } else if (averageHoldTime < 2 && totalTokensCreated > 3) {
    badges.push('paper_hands');
  }

  if (totalTokensCreated > 10) {
    badges.push('high_volume');
  }

  if (rugCount === 0 && totalTokensCreated >= 3) {
    badges.push('trusted');
  }

  return badges;
}

/**
 * Determine risk level from reputation score
 */
export function getRiskLevel(score: number): 'low' | 'medium' | 'high' | 'critical' {
  if (score >= 70) return 'low';
  if (score >= 50) return 'medium';
  if (score >= 30) return 'high';
  return 'critical';
}

/**
 * Get risk level color
 */
export function getRiskColor(level: 'low' | 'medium' | 'high' | 'critical'): string {
  switch (level) {
    case 'low': return '#00ff88';
    case 'medium': return '#ffb800';
    case 'high': return '#ff8800';
    case 'critical': return '#ff3b3b';
  }
}

// ============================================================================
// Mock Data (In production, fetch from API)
// ============================================================================

/**
 * Fetch creator profile
 * In production, this would call the SKOP API
 */
export async function getCreatorProfile(creatorAddress: string): Promise<CreatorProfile | null> {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 300));

  // Generate mock profile based on address
  const addressNum = parseInt(creatorAddress.slice(-6), 16);
  const isRugger = addressNum % 5 === 0;
  const isNew = addressNum % 10 === 0;

  const totalTokensCreated = isNew ? 1 : (addressNum % 15) + 1;
  const rugCount = isRugger ? Math.floor(totalTokensCreated * 0.4) : Math.floor(totalTokensCreated * 0.1);
  const successfulTokens = totalTokensCreated - rugCount;
  const averageHoldTime = isRugger ? 2 : (addressNum % 100) + 12;

  const profile: Partial<CreatorProfile> = {
    address: creatorAddress,
    totalTokensCreated,
    rugCount,
    successfulTokens,
    averageHoldTime,
    totalVolume: addressNum * 1000,
    firstTokenDate: Date.now() - (totalTokensCreated * 7 * 24 * 60 * 60 * 1000),
    lastTokenDate: Date.now() - (addressNum % 7) * 24 * 60 * 60 * 1000,
    verified: addressNum % 20 === 0,
  };

  profile.reputationScore = calculateReputationScore(profile);
  profile.badges = determineBadges(profile);

  return profile as CreatorProfile;
}

/**
 * Get creator's token history
 */
export async function getCreatorTokens(creatorAddress: string): Promise<CreatorToken[]> {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 200));

  const profile = await getCreatorProfile(creatorAddress);
  if (!profile) return [];

  // Generate mock tokens
  const tokens: CreatorToken[] = [];

  for (let i = 0; i < profile.totalTokensCreated; i++) {
    const isRugged = i < profile.rugCount;
    const daysSinceCreation = (profile.totalTokensCreated - i) * 3;

    tokens.push({
      address: `${creatorAddress.slice(0, 8)}token${i}`,
      name: `Token ${i + 1}`,
      symbol: `TKN${i + 1}`,
      createdAt: Date.now() - daysSinceCreation * 24 * 60 * 60 * 1000,
      platform: i % 2 === 0 ? 'pump.fun' : 'letsbonk',
      status: isRugged ? 'rugged' : (i < profile.successfulTokens ? 'graduated' : 'active'),
      peakMarketCap: Math.random() * 500000 + 10000,
      currentMarketCap: isRugged ? 0 : Math.random() * 100000,
      creatorHoldPercent: isRugged ? 0 : Math.random() * 10,
      creatorSoldAt: isRugged ? profile.averageHoldTime : undefined,
      profitPercent: isRugged ? -100 : Math.random() * 500 - 50,
    });
  }

  return tokens;
}

/**
 * Get creator stats summary
 */
export async function getCreatorStats(creatorAddress: string): Promise<CreatorStats | null> {
  const profile = await getCreatorProfile(creatorAddress);
  if (!profile) return null;

  const successRate = profile.totalTokensCreated > 0
    ? ((profile.successfulTokens / profile.totalTokensCreated) * 100)
    : 0;

  return {
    avgRugTime: profile.rugCount > 0 ? profile.averageHoldTime / 2 : 0,
    avgHoldTime: profile.averageHoldTime,
    successRate,
    avgProfitPercent: successRate > 50 ? Math.random() * 200 : -(Math.random() * 50),
    riskLevel: getRiskLevel(profile.reputationScore),
  };
}

// ============================================================================
// Storage for User Notes
// ============================================================================

const CREATOR_NOTES_KEY = 'skop_creator_notes';

/**
 * Get user's notes on a creator
 */
export function getCreatorNotes(userWallet: string, creatorAddress: string): string | null {
  try {
    const stored = localStorage.getItem(`${CREATOR_NOTES_KEY}_${userWallet}`);
    if (!stored) return null;
    const notes = JSON.parse(stored);
    return notes[creatorAddress] || null;
  } catch {
    return null;
  }
}

/**
 * Save user's notes on a creator
 */
export function saveCreatorNotes(
  userWallet: string,
  creatorAddress: string,
  note: string
): void {
  try {
    const stored = localStorage.getItem(`${CREATOR_NOTES_KEY}_${userWallet}`);
    const notes = stored ? JSON.parse(stored) : {};
    notes[creatorAddress] = note;
    localStorage.setItem(`${CREATOR_NOTES_KEY}_${userWallet}`, JSON.stringify(notes));
  } catch {
    console.error('Failed to save creator notes');
  }
}

// ============================================================================
// Blocked Creators
// ============================================================================

const BLOCKED_CREATORS_KEY = 'skop_blocked_creators';

/**
 * Get blocked creators
 */
export function getBlockedCreators(userWallet: string): string[] {
  try {
    const stored = localStorage.getItem(`${BLOCKED_CREATORS_KEY}_${userWallet}`);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Block a creator
 */
export function blockCreator(userWallet: string, creatorAddress: string): void {
  const blocked = getBlockedCreators(userWallet);
  if (!blocked.includes(creatorAddress)) {
    blocked.push(creatorAddress);
    localStorage.setItem(
      `${BLOCKED_CREATORS_KEY}_${userWallet}`,
      JSON.stringify(blocked)
    );
  }
}

/**
 * Unblock a creator
 */
export function unblockCreator(userWallet: string, creatorAddress: string): void {
  const blocked = getBlockedCreators(userWallet);
  const filtered = blocked.filter(a => a !== creatorAddress);
  localStorage.setItem(
    `${BLOCKED_CREATORS_KEY}_${userWallet}`,
    JSON.stringify(filtered)
  );
}

/**
 * Check if creator is blocked
 */
export function isCreatorBlocked(userWallet: string, creatorAddress: string): boolean {
  return getBlockedCreators(userWallet).includes(creatorAddress);
}
